import {
  type AnyVariables,
  createRequest,
  type GraphQLRequest,
  type OperationContext,
} from '@urql/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type UseQueryArgs,
  type UseQueryExecute,
  type UseQueryState,
  useClient,
} from 'urql';
import { pipe, subscribe } from 'wonka';

import {
  registerChartRequest,
  unregisterChartRequest,
} from '../../../../../shared/data-inspector/chart-request-registry';
import { useSafeChart } from '../../Chart/ChartProvider';
import { useIsTabVisible } from '../../TabVisibilityContext';
import { DataProviderQueryStatus } from '../types';

/**
 * Best-effort technical string for logging/telemetry (e.g. global AI chart preview).
 * Standard chart error UI omits this and shows generic copy instead.
 */
export function formatProviderErrorMessage(error: unknown): string | undefined {
  if (error === null || error === undefined) {
    return undefined;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error && error.message.trim() !== '') {
    return error.message;
  }
  const r = error as {
    graphQLErrors?: readonly { message?: string }[];
    networkError?: { message?: string };
    message?: string;
  };
  const gq = r.graphQLErrors
    ?.map((e) => e.message)
    .filter((m): m is string => typeof m === 'string' && m.trim() !== '')
    .join('; ');
  if (gq) {
    return gq;
  }
  if (
    r.networkError &&
    typeof r.networkError.message === 'string' &&
    r.networkError.message.trim() !== ''
  ) {
    return r.networkError.message;
  }
  if (typeof r.message === 'string' && r.message.trim() !== '') {
    return r.message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

type UrqlResultState<Data, Variables extends AnyVariables> = UseQueryState<
  Data,
  Variables
> & {
  key: number | null;
};

const RESULT_CACHE_LIMIT = 500;
const lastResultByRequestKey = new Map<
  number,
  UseQueryState<unknown, AnyVariables>
>();

const rememberResult = (
  key: number,
  result: UseQueryState<unknown, AnyVariables>
) => {
  if (lastResultByRequestKey.has(key)) lastResultByRequestKey.delete(key);
  lastResultByRequestKey.set(key, result);
  if (lastResultByRequestKey.size > RESULT_CACHE_LIMIT) {
    const oldest = lastResultByRequestKey.keys().next().value;
    if (oldest !== undefined) lastResultByRequestKey.delete(oldest);
  }
};

const readRememberedResult = <Data, Variables extends AnyVariables>(
  key: number
): UseQueryState<Data, Variables> | undefined =>
  lastResultByRequestKey.get(key) as UseQueryState<Data, Variables> | undefined;

export const clearRememberedQueryResults = () => lastResultByRequestKey.clear();

const isSameResult = <Data, Variables extends AnyVariables>(
  a: UrqlResultState<Data, Variables>,
  b: UrqlResultState<Data, Variables>
) =>
  a.key === b.key &&
  a.data === b.data &&
  a.error === b.error &&
  a.extensions === b.extensions &&
  a.fetching === b.fetching &&
  a.stale === b.stale &&
  a.hasNext === b.hasNext &&
  a.operation?.key === b.operation?.key;

const noResult = <Data, Variables extends AnyVariables>(): UrqlResultState<
  Data,
  Variables
> => ({
  key: null,
  fetching: false,
  stale: false,
  data: undefined,
  error: undefined,
  extensions: undefined,
  operation: undefined,
  hasNext: false,
});

export const useUrqlQueryWithStatus = <
  Data = unknown,
  Variables extends object = object,
>(
  args: UseQueryArgs<Variables, Data>
) => {
  // Tabs kept mounted-but-hidden by `KeepAliveTabGrids` report `false` here, so
  // their queries pause instead of refetching in the background. When the tab is
  // shown again the query resumes and reads straight from the urql cache.
  const isTabVisible = useIsTabVisible();

  // Every chart provider funnels through this hook, and all of them render
  // inside a ChartProvider — so this is the one place that can tell the Data
  // Inspector which card a query belongs to, with no call-site changes.
  const { item } = useSafeChart();
  const chartItemId = item?.id;

  const client = useClient();

  const requestRef = useRef<GraphQLRequest<Data, Variables>>();
  const request = useMemo(() => {
    const next = createRequest<Data, Variables>(
      args.query,
      args.variables as Variables
    );
    if (
      requestRef.current !== undefined &&
      requestRef.current.key === next.key
    ) {
      return requestRef.current;
    }
    requestRef.current = next;
    return next;
  }, [args.query, args.variables]);
  const requestKey = request.key;

  // urql dedupes identical (query, variables) in cacheExchange, which is BEFORE
  // the recorder — so the exchange sees one operation even when several cards
  // share it. Registering the card against urql's own request key is what lets
  // the drawer say (and highlight) every chart a request feeds.
  useEffect(() => {
    if (!chartItemId) return;
    registerChartRequest(requestKey, chartItemId);
    return () => unregisterChartRequest(requestKey, chartItemId);
  }, [requestKey, chartItemId]);

  const isPaused = Boolean(args.pause) || !isTabVisible;
  const { requestPolicy } = args;
  const contextKey = JSON.stringify(args.context ?? null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by contextKey
  const context = useMemo(() => args.context, [contextKey]);

  const [reexecution, setReexecution] = useState<{
    count: number;
    key: number | null;
    context?: Partial<OperationContext>;
  }>({ count: 0, key: null });

  const [received, setReceived] = useState<UrqlResultState<Data, Variables>>(
    () => noResult<Data, Variables>()
  );

  useEffect(() => {
    if (isPaused) return;

    const isReexecution =
      reexecution.count > 0 && reexecution.key === requestKey;
    const operationContext: Partial<OperationContext> = {
      requestPolicy,
      ...context,
      ...(isReexecution
        ? { requestPolicy: 'network-only', ...reexecution.context }
        : {}),
    };

    let receivedSync = false;
    const subscription = pipe(
      client.executeQuery<Data, Variables>(request, operationContext),
      subscribe((result) => {
        receivedSync = true;
        setReceived((prev) => {
          const next: UrqlResultState<Data, Variables> = {
            key: requestKey,
            operation: result.operation,
            data:
              result.data !== undefined || result.error
                ? result.data
                : prev.data,
            error: result.error,
            extensions: result.extensions,
            stale: Boolean(result.stale),
            hasNext: Boolean(result.hasNext),
            fetching: false,
          };
          if (next.data !== undefined && next.error === undefined) {
            rememberResult(requestKey, next);
          }
          return isSameResult(prev, next) ? prev : next;
        });
      })
    );

    if (!receivedSync) {
      setReceived((prev) =>
        prev.key === requestKey && !prev.fetching
          ? { ...prev, fetching: true }
          : prev
      );
    }

    return () => subscription.unsubscribe();
  }, [
    client,
    request,
    requestKey,
    isPaused,
    requestPolicy,
    context,
    reexecution,
  ]);

  const executeQuery: UseQueryExecute = useCallback(
    (opts) => {
      setReexecution((prev) => ({
        count: prev.count + 1,
        key: requestKey,
        context: opts,
      }));
    },
    [requestKey]
  );

  const urqlState: UseQueryState<Data, Variables> = (() => {
    if (received.key === requestKey) {
      return isPaused && received.fetching
        ? { ...received, fetching: false }
        : received;
    }
    const remembered = readRememberedResult<Data, Variables>(requestKey);
    if (remembered !== undefined) {
      return { ...remembered, fetching: false };
    }
    return {
      data: received.data,
      error: undefined,
      extensions: undefined,
      operation: undefined,
      stale: false,
      hasNext: false,
      fetching: !isPaused,
    };
  })();
  const { data, fetching, error } = urqlState;

  // Derive status from urql result state. GraphQL may return partial `data` with
  // top-level `errors`; urql still sets `error` in that case. Prefer Success when
  // we have any usable payload so charts render instead of showing Data Unavailable,
  // but fall through to Error when every top-level field is null so a true failure
  // (e.g. `{ composition_v3: null }` + errors) still surfaces the error UI.
  const status = (() => {
    if (fetching) {
      return DataProviderQueryStatus.Fetching;
    }
    const dataHasUsablePayload =
      data !== undefined &&
      data !== null &&
      (typeof data !== 'object' ||
        Object.values(data).some((v) => v !== null && v !== undefined));
    if (dataHasUsablePayload) {
      return DataProviderQueryStatus.Success;
    }
    if (error) {
      return DataProviderQueryStatus.Error;
    }
    if (data !== undefined && data !== null) {
      return DataProviderQueryStatus.Success;
    }
    return DataProviderQueryStatus.Idle;
  })();

  // Return enhanced state with status
  const enhancedState: UseQueryState<Data, Variables> & {
    status: DataProviderQueryStatus;
  } = {
    ...urqlState,
    status,
  };

  return [enhancedState, executeQuery] as const;
};

export const combineQueryStatus = (
  statuses: DataProviderQueryStatus[] = []
): DataProviderQueryStatus => {
  if (statuses.includes(DataProviderQueryStatus.Error)) {
    return DataProviderQueryStatus.Error;
  }
  if (statuses.includes(DataProviderQueryStatus.Fetching)) {
    return DataProviderQueryStatus.Fetching;
  }
  if (statuses.every((status) => status === DataProviderQueryStatus.Success)) {
    return DataProviderQueryStatus.Success;
  }
  return DataProviderQueryStatus.Idle;
};
